import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Textarea,
  buttonStyles,
} from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { CompanyPage, QuoteDetail, QuoteRow } from '../lib/types';
import { LinesEditor, linesPayload, type FormLine } from './order-tools';

/**
 * Bayi teklifleri (F7-06, ADR-109): istekler ve taslaklar; satır fiyatı, geçerlilik ve not; gönderim (değiştirilemez
 * sürüm), iptal ve yazdırma. Bayi vitrinde kabul edince teklif fiyatıyla sipariş verir.
 */

const STATUSES = ['requested', 'draft', 'sent', 'ordered', 'rejected', 'cancelled'] as const;
const TONE: Record<string, 'amber' | 'neutral' | 'pine' | 'moss' | 'rose' | 'slate'> = {
  requested: 'amber',
  draft: 'neutral',
  sent: 'pine',
  ordered: 'moss',
  rejected: 'rose',
  cancelled: 'slate',
};

function StatusBadge({ status, expired }: { status: string; expired: boolean }) {
  const t = useT();
  return expired ? (
    <Badge tone="rose">{t('admin.quotes.expired')}</Badge>
  ) : (
    <Badge tone={TONE[status] ?? 'neutral'}>{t(`admin.quotes.statuses.${status}`)}</Badge>
  );
}

export function QuotesPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  const list = useQuery({
    queryKey: ['quotes', status, q.trim()],
    queryFn: () => api.get<QuoteRow[]>(`/v1/admin/quotes?${params}`),
  });
  return (
    <PageFrame
      helpKey="page.quotes"
      actions={
        can('b2b.quote.manage') ? (
          <span className="inline-flex items-center gap-1">
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('admin.quotes.new')}
            </Button>
            <HelpTip k="quotes.new" />
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.quotes.status')} help={<HelpTip k="quotes.status_filter" />}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.quotes.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.quotes.search')} help={<HelpTip k="quotes.search" />}>
          {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
        </Field>
      </div>
      {!list.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data.length ? (
        <EmptyState title={t('admin.quotes.empty')} description={t('admin.quotes.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[44rem] text-sm">
            <caption className="sr-only">{t('admin.quotes.listCaption')}</caption>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.number')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.dealer')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.status')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.quotes.total')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.validUntil')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.createdAt')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    <Link
                      to="/teklifler/$id"
                      params={{ id: r.id }}
                      className="font-medium text-pine underline underline-offset-2"
                    >
                      {r.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {r.legalName}
                    {r.customerName && <span className="block text-ink-3">{r.customerName}</span>}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} expired={r.expired} />
                  </td>
                  <td className="px-3 py-2 text-end tabular">
                    {r.totalMinor !== null ? money(r.totalMinor, r.currency) : '—'}
                  </td>
                  <td className="px-3 py-2">{r.validUntil ? formatDate(r.validUntil, false) : '—'}</td>
                  <td className="px-3 py-2">{formatDate(r.createdAt, false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewQuoteDialog onClose={() => setCreating(false)} />}
    </PageFrame>
  );
}

function NewQuoteDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const choice = useStoreChoice();
  const [q, setQ] = useState('');
  const [company, setCompany] = useState<{ id: string; legalName: string } | null>(null);
  const companies = useQuery({
    queryKey: ['companies', 'pick', q],
    queryFn: () =>
      api.get<CompanyPage>(`/v1/admin/companies?status=approved&limit=8&q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim().length >= 2 && !company,
  });
  const create = useApiMutation(
    () =>
      api.post<QuoteDetail>('/v1/admin/quotes', {
        storeId: choice.storeId,
        companyId: company!.id,
        lines: [],
      }),
    {
      invalidate: [['quotes']],
      onSuccess: (r) => {
        onClose();
        void navigate({ to: '/teklifler/$id', params: { id: r.id } });
      },
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.quotes.new')}
      description={t('admin.quotes.newLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!company || !choice.storeId}
            onClick={() => create.mutate(undefined)}
          >
            {t('admin.quotes.create')}
          </Button>
        </>
      }
    >
      {company ? (
        <div className="flex items-center justify-between rounded-sm border border-line px-3 py-2">
          <span className="font-medium">{company.legalName}</span>
          <Button size="sm" variant="ghost" onClick={() => setCompany(null)}>
            {t('admin.quotes.changeDealer')}
          </Button>
        </div>
      ) : (
        <>
          <Field label={t('admin.quotes.dealer')} help={<HelpTip k="quotes.dealer" />}>
            {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
          </Field>
          {companies.data && (
            <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm">
              {companies.data.items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-start hover:bg-surface-2"
                    onClick={() => setCompany({ id: c.id, legalName: c.legalName })}
                  >
                    {c.legalName} {c.externalCode && <span className="text-ink-3">{c.externalCode}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Dialog>
  );
}

const linesOf = (d: QuoteDetail): FormLine[] =>
  d.lines.map((l) => ({
    variantId: l.variantId,
    label: `${l.title} (${l.sku})`,
    quantity: String(l.quantity),
    unitPrice: minorToInput(l.unitPriceMinor),
  }));

export function QuoteDetailPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/teklifler/$id' });
  const q = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<QuoteDetail>(`/v1/admin/quotes/${id}`),
  });
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <QuoteEditor key={`${q.data.id}:${q.data.version}`} d={q.data} />;
}

function QuoteEditor({ d }: { d: QuoteDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const open = ['requested', 'draft', 'sent'].includes(d.status);
  const manage = can('b2b.quote.manage') && open;
  const [lines, setLines] = useState<FormLine[]>(() => linesOf(d));
  const [validUntil, setValidUntil] = useState(d.validUntil ?? '');
  const [note, setNote] = useState(d.note ?? '');
  const [internalNote, setInternalNote] = useState(d.internalNote ?? '');
  const [cancelling, setCancelling] = useState(false);
  const body = () => ({
    lines: linesPayload(lines, true, d.currency),
    validUntil: validUntil || null,
    note: note.trim() || null,
    internalNote: internalNote.trim() || null,
    version: d.version,
  });
  const save = useApiMutation(() => api.patch<QuoteDetail>(`/v1/admin/quotes/${d.id}`, body()), {
    invalidate: [['quote', d.id], ['quotes']],
    success: t('admin.quotes.saved'),
    silentFieldErrors: true,
  });
  const send = useApiMutation(
    async () => {
      await api.patch(`/v1/admin/quotes/${d.id}`, body());
      return api.post<{ delivery: 'email' | 'manual' }>(`/v1/admin/quotes/${d.id}/send`);
    },
    {
      invalidate: [['quote', d.id], ['quotes']],
      silentFieldErrors: true,
      onSuccess: (r) =>
        toast.success(r.delivery === 'email' ? t('admin.quotes.sentEmailed') : t('admin.quotes.sentNoEmail')),
    },
  );
  const err = send.error ?? save.error;
  const sentStatus = d.status === 'sent' && d.expired;
  return (
    <PageFrame
      helpKey="page.quote_detail"
      title={t('admin.quotes.title', { number: d.number })}
      actions={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Link to="/teklifler" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.quotes.backToList')}
          </Link>
          <span className="inline-flex items-center gap-1">
            <Button onClick={() => window.print()}>{t('admin.quotes.print')}</Button>
            <HelpTip k="quotes.print" />
          </span>
          {manage && (
            <>
              <span className="inline-flex items-center gap-1">
                <Button loading={save.isPending} onClick={() => save.mutate(undefined)}>
                  {t('common.save')}
                </Button>
                <HelpTip k="quotes.save" />
              </span>
              <span className="inline-flex items-center gap-1">
                <Button
                  variant="primary"
                  loading={send.isPending}
                  disabled={!lines.length || !validUntil}
                  onClick={() => send.mutate(undefined)}
                >
                  {d.currentVersion ? t('admin.quotes.resend') : t('admin.quotes.send')}
                </Button>
                <HelpTip k="quotes.send" />
              </span>
              <span className="inline-flex items-center gap-1">
                <Button variant="danger" onClick={() => setCancelling(true)}>
                  {t('admin.quotes.cancel')}
                </Button>
                <HelpTip k="quotes.cancel" />
              </span>
            </>
          )}
        </div>
      }
    >
      <Section title={t('admin.quotes.summary')}>
        <div className="mb-3">
          <StatusBadge status={d.status} expired={sentStatus} />
        </div>
        <KeyValue
          items={[
            {
              label: t('admin.quotes.dealer'),
              value: (
                <Link
                  to="/bayiler/$id"
                  params={{ id: d.company.id }}
                  className="text-pine underline underline-offset-2"
                >
                  {d.company.legalName}
                </Link>
              ),
            },
            {
              label: t('admin.quotes.requester'),
              value: d.customer ? `${d.customer.name} · ${d.customer.email}` : '—',
            },
            { label: t('admin.quotes.requestNote'), value: d.requestNote ?? '—' },
            {
              label: t('admin.quotes.version'),
              value: d.currentVersion ? t('admin.quotes.versionValue', { version: d.currentVersion }) : '—',
            },
            ...(d.status === 'rejected'
              ? [{ label: t('admin.quotes.rejectReason'), value: d.rejectReason ?? '—' }]
              : []),
            ...(d.status === 'cancelled'
              ? [{ label: t('admin.quotes.cancelReason'), value: d.cancelReason ?? '—' }]
              : []),
            ...(d.orderId
              ? [
                  {
                    label: t('admin.quotes.order'),
                    value: (
                      <Link
                        to="/siparisler/$id"
                        params={{ id: d.orderId }}
                        className="text-pine underline underline-offset-2"
                      >
                        {t('admin.quotes.openOrder')}
                      </Link>
                    ),
                  },
                ]
              : []),
          ]}
        />
        {d.status === 'sent' && <p className="mt-3 text-sm text-ink-2">{t('admin.quotes.editResetsLead')}</p>}
      </Section>

      <Section title={t('admin.quotes.lines')} description={t('admin.quotes.linesLead')}>
        {manage && (
          <div className="mb-4 print:hidden">
            <LinesEditor lines={lines} onChange={setLines} canPrice currency={d.currency} />
          </div>
        )}
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm">
            <caption className="sr-only">{t('admin.quotes.lines')}</caption>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.quotes.product')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.quotes.qty')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.quotes.listPrice')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.quotes.unitPrice')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.quotes.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.variantId} className="border-t border-line">
                  <td className="px-3 py-2">
                    {l.title}
                    <span className="block text-ink-3">{l.sku}</span>
                  </td>
                  <td className="px-3 py-2 text-end tabular">{l.quantity}</td>
                  <td className="px-3 py-2 text-end tabular">{money(l.listPriceMinor, d.currency)}</td>
                  <td className="px-3 py-2 text-end tabular font-medium">
                    {money(l.unitPriceMinor, d.currency)}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(l.lineTotalMinor, d.currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-line bg-surface-2/50">
              <tr>
                <td className="px-3 py-2" colSpan={4}>
                  {t('admin.quotes.taxIncluded', { tax: money(d.totals.taxMinor, d.currency) })}
                  {d.totals.listTotalMinor > d.totals.totalMinor && (
                    <span className="block text-ink-3">
                      {t('admin.quotes.saving', {
                        amount: money(d.totals.listTotalMinor - d.totals.totalMinor, d.currency),
                      })}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-end tabular font-semibold">
                  {money(d.totals.totalMinor, d.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {manage && <p className="mt-2 text-sm text-ink-3 print:hidden">{t('admin.quotes.saveToRecalc')}</p>}
      </Section>

      <Section title={t('admin.quotes.terms')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.quotes.validUntil')}
            help={<HelpTip k="quotes.valid_until" />}
            error={fieldError(err, 'validUntil')}
          >
            {(p) => (
              <Input
                {...p}
                type="date"
                disabled={!manage}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            )}
          </Field>
          <div />
          <Field
            label={t('admin.quotes.note')}
            optionalLabel={t('admin.quotes.noteHint')}
            help={<HelpTip k="quotes.note" />}
          >
            {(p) => (
              <Textarea
                {...p}
                className="min-h-20"
                disabled={!manage}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.quotes.internalNote')}
            optionalLabel={t('admin.quotes.internalNoteHint')}
            help={<HelpTip k="quotes.internal_note" />}
            className="print:hidden"
          >
            {(p) => (
              <Textarea
                {...p}
                className="min-h-20"
                disabled={!manage}
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
              />
            )}
          </Field>
        </div>
        {fieldError(err, 'lines') && (
          <p role="alert" className="mt-2 text-sm text-rose">
            {fieldError(err, 'lines')}
          </p>
        )}
      </Section>

      {d.versions.length > 0 && (
        <Section title={t('admin.quotes.versions')} className="print:hidden">
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {d.versions.map((v) => (
              <li key={v.version} className="flex flex-wrap justify-between gap-2 px-4 py-2">
                <span>
                  {t('admin.quotes.versionValue', { version: v.version })} · {formatDate(v.sentAt, true)}
                </span>
                <span className="tabular">
                  {money(v.totals.totalMinor, d.currency)} ·{' '}
                  {t('admin.quotes.validUntilShort', { date: formatDate(v.validUntil, false) })}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {cancelling && <CancelDialog id={d.id} onClose={() => setCancelling(false)} />}
    </PageFrame>
  );
}

function CancelDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const [reason, setReason] = useState('');
  const cancel = useApiMutation(() => api.post(`/v1/admin/quotes/${id}/cancel`, { reason }), {
    invalidate: [['quote', id], ['quotes']],
    success: t('admin.quotes.cancelled'),
    onSuccess: onClose,
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.quotes.cancelTitle')}
      description={t('admin.quotes.cancelLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={cancel.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => cancel.mutate(undefined)}
          >
            {t('admin.quotes.cancel')}
          </Button>
        </>
      }
    >
      <Field label={t('admin.quotes.cancelReason')} help={<HelpTip k="quotes.cancel_reason" />}>
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
